import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { PaperclipIcon, PlusIcon, Trash2Icon } from "@lucide/icons";

import type { OnCompleteParams } from "simplydo/react/components/Choosers/FileChooser/uploadUtils";
import { Button } from "simplydo/react/ui/button";
import { H2, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Table, TableCell, TableRow } from "simplydo/react/ui/table";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { FileChooser } from "components/lib/Choosers";
import { EmptyBox } from "components/lib/UI";

import { useChildIdea } from "./hooks/useIdea";
import type { IdeaAttachmentWithUrl, IdeaDetail, UpdateIdeaFn } from "./types";

type IdeaAttachmentsProps = { isEditing?: boolean; updateIdea: UpdateIdeaFn };

const IdeaAttachments = ({ isEditing, updateIdea }: IdeaAttachmentsProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const [idea] = useChildIdea({
    select: (data) => ({
      _id: data._id,
      attachments: data.attachments,
      ownerChallenge: data.ownerChallenge,
      challenge: data.challenge,
      user: data.user,
      collaborators: data.collaborators,
      isSubmitted: data.isSubmitted,
    }),
  });
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const addAttachments = useCallback(
    (files: OnCompleteParams[]) => {
      if (!idea._id) {
        return;
      }
      setUploadingAttachment(true);

      api.ideas.createMultipleAttachments(
        idea._id,
        {
          attachments: files.map((file) => ({
            fileName: file.fileName,
            savedName: file.savedName,
          })),
        },
        ({ attachments: newAttachments }) => {
          setUploadingAttachment(false);
          updateIdea(newAttachments, ["attachments"], true);
          toast.success("File attached successfully");
        },
        (err) => {
          toast.error(err.message);
          setUploadingAttachment(false);
        },
      );
    },
    [idea._id, updateIdea],
  );

  const deleteAttachment = useCallback(
    (uuid: string) => {
      if (!idea._id) {
        return;
      }
      setUploadingAttachment(true);

      api.ideas.deleteAttachment(
        idea._id,
        uuid,
        ({ attachments: newAttachments }) => {
          setUploadingAttachment(false);
          updateIdea(newAttachments, ["attachments"], true);
          toast("File deleted successfully");
        },
        (err) => {
          toast.error(err.message);
          setUploadingAttachment(false);
        },
      );
    },
    [idea._id, updateIdea],
  );

  const canEdit = util.canEditIdea(user, idea as IdeaDetail);

  const fileChooserProps = {
    forType: "idea",
    forId: idea._id ?? "",
    onCompleteMultiple: addAttachments,
    allowMultiple: true as const,
  };

  return (
    <div>
      <H4>{t("generic.attachments")}</H4>
      {idea?.attachments && idea?.attachments.length === 0 && (
        <EmptyBox>
          <H2>
            {t("ideas.attachments.none")}
            <Subheader>{t("ideas.attachments.info")}</Subheader>
          </H2>
          <FileChooser
            {...fileChooserProps}
            trigger={
              <Button size="small">
                <Icon icon={PlusIcon} />
                {t("ideas.actions.addAttachment")}
              </Button>
            }
          />
        </EmptyBox>
      )}
      <Table>
        {idea?.attachments?.map((a: IdeaAttachmentWithUrl) => (
          <TableRow key={a.uuid}>
            <TableCell>
              <a
                href={a.url ?? a.value}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon icon={PaperclipIcon} /> {a.name}
              </a>
            </TableCell>
            {canEdit && isEditing && (
              <TableCell textAlign="right">
                <Button
                  loading={uploadingAttachment}
                  onClick={() => deleteAttachment(a.uuid)}
                  variant="outline"
                >
                  <Icon icon={Trash2Icon} />
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </Table>
      {canEdit && isEditing ? (
        <FileChooser
          {...fileChooserProps}
          trigger={
            <Button size="small">
              <Icon icon={PlusIcon} />
              {t("ideas.actions.addAttachment")}
            </Button>
          }
        />
      ) : null}
    </div>
  );
};

export default IdeaAttachments;
